import { useCallback, useState } from "react";

function readStoredCollapsed(storageKey: string): Set<string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((id): id is string => typeof id === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

function writeStoredCollapsed(storageKey: string, collapsed: Set<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...collapsed]));
  } catch {
    // Private-mode / quota failures are not worth surfacing — sections just
    // reopen on the next reload.
  }
}

/**
 * Which filter-sidebar sections are folded down to their title, remembered per
 * browser under `storageKey`. Every section starts expanded. See
 * [[collapsible-section.spec]].
 */
export function useCollapsedSections(storageKey: string): {
  isCollapsed: (section: string) => boolean;
  toggle: (section: string) => void;
} {
  const [collapsed, setCollapsed] = useState(() =>
    readStoredCollapsed(storageKey),
  );

  const isCollapsed = useCallback(
    (section: string) => collapsed.has(section),
    [collapsed],
  );

  const toggle = useCallback(
    (section: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(section)) next.delete(section);
        else next.add(section);
        writeStoredCollapsed(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  return { isCollapsed, toggle };
}
